<template>
  <div v-if="shouldRender" class="company-field-approval-results">
    <ProductApprovalResultField
      v-for="result in displayResults"
      :key="`company_ar_${result.approvalRequestResultId || result.usageType}_${result.usageId ?? ''}`"
      :from-admin="isApprovalEditable"
      :draft="host.getCompanyApprovalResultDraft(result)"
      :saved-reason="result.rejectReason"
      :result-code="result.resultCode"
      :result-key="host.getCompanyApprovalResultDraftKey(result)"
      :can-save="false"
      :saving="false"
      :force-show="isApprovalEditable"
      :show-reject-reason="shouldShowRejectReason"
      :show-save="false"
      :templates="fieldRejectTemplates"
      :actor="fieldRejectActor"
      :error="host.getFieldRejectErrorMessage ? host.getFieldRejectErrorMessage(result) : ''"
      @update:draft="host.setCompanyApprovalResultDraft(result, $event)"
      @update:resultCode="host.setCompanyApprovalResultCode(result, $event)"
      @clear-error="host.clearFieldRejectError && host.clearFieldRejectError(result)"
    />
  </div>
</template>

<script>
import ProductApprovalResultField from '../NewproductInfoCenter/ProductApprovalResultField.vue';
import {
  isCompanyApprovalRejectedRow,
  resolveCompanyRejectReasonText,
} from './companyApprovalFieldMeta';
import { SHOW_EDIT_SUPPLIER_REJECT_REASON } from '@/constants/approvalRejectUi.js';

export default {
  name: 'CompanyFieldApprovalResults',
  components: { ProductApprovalResultField },
  inject: {
    companyApprovalHost: { default: null },
  },
  props: {
    /** 字段键，对应 Approval_Request_Content.UsageType */
    usageType: { type: [String, Array], required: true },
    /** 列表字段 UsageId（如认证行 RID）；主表字段不传 */
    usageId: { default: null },
  },
  computed: {
    host() {
      return this.companyApprovalHost;
    },
    isApprovalEditable() {
      return !!(this.host?.fromAdmin && this.host?.isApprovalMode);
    },
    usageTypes() {
      const raw = this.usageType;
      if (Array.isArray(raw)) {
        return raw.map((x) => String(x || '').trim()).filter(Boolean);
      }
      return String(raw || '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
    },
    hasUsageIdFilter() {
      return this.usageId != null && String(this.usageId).trim() !== '';
    },
    /** 父级传了 :usage-id（列表行）；未传则按主表字段处理 */
    isListRowBound() {
      return Object.prototype.hasOwnProperty.call(this.$options.propsData || {}, 'usageId');
    },
    results() {
      if (!this.host || !this.usageTypes.length) return [];
      const rows = this.host.getCompanyApprovalResultsByUsageType(this.usageTypes);
      if (this.isListRowBound) {
        if (!this.hasUsageIdFilter) return [];
        const key = String(this.usageId).trim();
        return rows.filter((row) => {
          const usageId = row?.usageId ?? row?.UsageId;
          return String(usageId ?? '').trim() === key;
        });
      }
      return rows;
    },
    resultsSelected() {
      return this.results.filter((row) => {
        if (!isCompanyApprovalRejectedRow(row)) return false;
        const draft = this.host?.getCompanyApprovalResultDraft
          ? this.host.getCompanyApprovalResultDraft(row)
          : '';
        const reason = resolveCompanyRejectReasonText(draft || row.rejectReason || '');
        return !!String(reason || '').trim();
      });
    },
    displayResults() {
      return this.isApprovalEditable ? this.results : this.resultsSelected;
    },
    shouldShowRejectReason() {
      if (this.isApprovalEditable) return this.host?.showCompanyRejectPanels === true;
      return this.host?.isCompanyRejectedStatus === true;
    },
    shouldRender() {
      if (!this.results.length) return false;
      if (this.host?.showCompanyRejectPanels === false) return false;
      if (this.isApprovalEditable) return true;
      // TEMP：编辑模式 / 供应商暂不展示驳回原因
      if (!SHOW_EDIT_SUPPLIER_REJECT_REASON) return false;
      if (!this.shouldShowRejectReason) return false;
      return this.resultsSelected.length > 0;
    },
    fieldRejectTemplates() {
      if (!this.isApprovalEditable) return [];
      return this.host?.overallRejectTemplates || [];
    },
    fieldRejectActor() {
      return this.host?.approvalRejectBy || 'AI';
    },
  },
};
</script>

<style lang="scss">
/* 勿加 z-index：会创建堆叠上下文，模板下拉菜单无法盖过下方表单 */
.company-field-approval-results {
  position: relative;
  clear: both;
  margin-top: 4px;
  margin-bottom: 16px;
  width: 100%;
  box-sizing: border-box;
}

.theme-form .form-group:has(+ .company-field-approval-results),
.form-group:has(+ .company-field-approval-results) {
  margin-bottom: 4px !important;
}

.theme-form .form-group:has(+ .company-field-approval-results) + .company-field-approval-results,
.form-group:has(+ .company-field-approval-results) + .company-field-approval-results {
  margin-top: 0;
}

/* 电话等四宫格：驳回框在独立 col-12，收紧上一行 col-6 的 form-group 底边距 */
.row:has(> .col-12 > .company-field-approval-results) > .col-6:nth-last-child(2) .form-group,
.row:has(> .col-12 > .company-field-approval-results) > .col-6:nth-last-child(3) .form-group {
  margin-bottom: 4px !important;
}

.row:has(> .col-12 > .company-field-approval-results) > .col-12 > .company-field-approval-results {
  margin-top: 0;
}
</style>
